import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAuthInvitation } from '../../services/auth/invitation.service';
import { ApiDigitalAuthRole } from '../../services/auth/role.service';
import { Error } from '../../models/error.model';
import { AppServiceToken } from '../../models/app.service.model';
import { Message } from '../../models/message.model';
import { Role } from '../../models/role.model';
import { GlobalSwal } from '../../styles/swal.style';
import { CompanyToken } from '../../models/company.model';
import { legendIconInvalid, legendIconValidInvalidRestart, legendInvalid, legendValid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { getError, getMessage } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';

import AppLegend from '../element/Legend';

export interface AppModalNotificationInvitationProps {
  appServiceForUser: AppServiceToken | null,
  companyForUser: CompanyToken | null,
  onInvitationSent?: () => void
};

let errorResponse: Error, messageResponse: Message;

const AppModalNotificationInvitation: React.FunctionComponent<AppModalNotificationInvitationProps> = ({appServiceForUser, companyForUser, onInvitationSent}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [email, setEmail] = useState({value: '', valid: false})
  const [roles, setRoles] = useState<Role[] | null>(null)
  const [selectedRoles, setSelectedRoles] = useState<{values: string[], valid: boolean}>({values: [], valid: false})
  const [roleError, setRoleError] = useState<string | null>(null)

  useEffect(() => {
    if (appServiceForUser?.api_key && companyForUser) {
      loadRoles(appServiceForUser.api_key)
    } else {
      setRoles([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser, companyForUser])

  const loadRoles = async (api_key: string) => {
    const response = await ApiDigitalAuthRole.getInvitableRoles(api_key)
    if (response.status === 200 || response.status === 304) {
      const loaded = Array.isArray(response.data) ? response.data as Role[] : []
      setRoles(loaded)
      if (loaded.length === 1) {
        setSelectedRoles({values: [loaded[0].id], valid: true})
      }
    } else {
      setRoles([])
      errorWebHandler(lang, {response})
    }
  }

  const handleToggleRole = (roleId: string) => {
    setSelectedRoles(prev => {
      const exists = prev.values.includes(roleId)
      const newValues = exists ? prev.values.filter(id => id !== roleId) : [...prev.values, roleId]
      return {values: newValues, valid: newValues.length > 0}
    })
  }

  const submitInvitationSend = async () => {
    setLoadIndicator('on')
    setRoleError(null)

    if (appServiceForUser && email.valid && selectedRoles.valid) {
      await ApiDigitalAuthInvitation.postInvitation(appServiceForUser.api_key, email.value, selectedRoles.values).then( (response) => {
        if (response.status === 201) {
          messageResponse = response.data

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.userWillRespondInvitation,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            onInvitationSent?.()
            executeHideModalCompanyInvitation()
          })
        } else {
          errorResponse = response.data

          if (errorResponse.code === 'INVITE_ROLE_FORBIDDEN') {
            setRoleError(errorResponse.message || lang.labels.roleAssignmentForbidden)
          } else {
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!email.value.length) legendIconInvalid('modal-invitation-create-input-email', 'modal-invitation-create-legend-email-required')
        if (!selectedRoles.valid) legendInvalid('modal-invitation-create-legend-roles-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const validateEmail = () => {
    evaluateLegendRequiredIcon(expressions.email, email, setEmail, 'modal-invitation-create-input-email', 'modal-invitation-create-legend-email-valid', 'modal-invitation-create-legend-email-required')
  }

  const executeHideModalCompanyInvitation = () => {
    modalHide('modal-invitation-create')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-invitation-create-input-email', 'modal-invitation-create-legend-email-valid')
    legendIconValidInvalidRestart('modal-invitation-create-input-email', 'modal-invitation-create-legend-email-required')
    legendValid('modal-invitation-create-legend-roles-required')

    setEmail({value: '', valid: false})
    setSelectedRoles({values: [], valid: false})
    setRoleError(null)
  }

  return (
    <div id="modal-invitation-create" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header pb-0 border-0 justify-content-end">
            <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={executeHideModalCompanyInvitation}>
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body pt-0">
            <div className="text-center mb-5">
              <h2 className="text-capitalize mb-3">{lang.labels.inviteAccessCompany}</h2>
              <div>
                {lang.labels.anInvitationWillBeSent}
                <strong className="mx-1">{companyForUser && companyForUser.name}</strong>
                .
              </div>
            </div>
            <div className="fw-bold w-100 mb-5">
              <div className="fs-6 fw-bolder">{lang.labels.doYouWantToSendInvitation}</div>
              <div className="fs-7 text-secondary text-justify">{lang.labels.enterEmailWhetherBelongsRegisteredUserOrNot}</div>
            </div>
            <div className="border-top border-bottom py-3">
              <div className="mb-5">
                <input id="modal-invitation-create-input-email" className="form-control" type="email" autoComplete="off" name="email" placeholder={lang.labels.emailAddress} value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
                <AppLegend component={"modal-invitation-create"} attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="mb-5">
                <label className="form-label required">{lang.labels.roles}</label>
                {!roles
                  ? <div className="text-muted">{lang.labels.loading}</div>
                  : roles.length === 0
                    ? <div className="text-muted">{lang.labels.noAssignableRoles}</div>
                    : (
                      <div className="d-flex flex-column gap-2 border rounded p-3">
                        {roles.map(role => (
                          <div key={role.id} className="form-check">
                            <input
                              id={`modal-invitation-create-role-${role.id}`}
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedRoles.values.includes(role.id)}
                              onChange={() => handleToggleRole(role.id)}
                            />
                            <label
                              htmlFor={`modal-invitation-create-role-${role.id}`}
                              className="form-check-label ms-2"
                            >
                              <span className="fw-bold">{role.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )
                }
                <AppLegend component={"modal-invitation-create"} attribute={{check: selectedRoles.valid, name: "roles", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                {roleError && <div className="text-danger fs-7 mt-2">{roleError}</div>}
              </div>
              <div className="d-flex flex-center">
                <button className="btn btn-light-primary w-100" type="button" data-kt-indicator={loadIndicator} onClick={submitInvitationSend}>
                  <span className="indicator-label">
                    <div className="d-flex align-items-center justify-content-center">
                      <span className="svg-icon svg-icon-5">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.43 8.56949L10.744 15.1395C10.6422 15.282 10.5804 15.4492 10.5651 15.6236C10.5498 15.7981 10.5815 15.9734 10.657 16.1315L13.194 21.4425C13.2737 21.6097 13.3991 21.751 13.5557 21.8499C13.7123 21.9488 13.8938 22.0014 14.079 22.0015H14.117C14.3087 21.9941 14.4941 21.9307 14.6502 21.8191C14.8062 21.7075 14.9261 21.5526 14.995 21.3735L21.933 3.33649C22.0011 3.15918 22.0164 2.96594 21.977 2.78013C21.9376 2.59432 21.8452 2.4239 21.711 2.28949L15.43 8.56949Z" fill="black"/>
                          <path opacity="0.3" d="M20.664 2.06648L2.62602 9.00148C2.44768 9.07085 2.29348 9.19082 2.1824 9.34663C2.07131 9.50244 2.00818 9.68731 2.00074 9.87853C1.99331 10.0697 2.04189 10.259 2.14054 10.4229C2.23919 10.5869 2.38359 10.7185 2.55601 10.8015L7.86601 13.3365C8.02383 13.4126 8.19925 13.4448 8.37382 13.4297C8.54839 13.4145 8.71565 13.3526 8.85801 13.2505L15.43 8.56548L21.711 2.28448C21.5762 2.15096 21.4055 2.05932 21.2198 2.02064C21.034 1.98196 20.8409 1.99788 20.664 2.06648Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.sendInvitation}
                    </div>
                  </span>
                  <span className="indicator-progress">
                    {lang.labels.pleaseWait}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalNotificationInvitation;
